import { debounce } from "lodash";
import { Delta } from 'quill';
import React, {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import ReactQuill, { EmitterSource } from 'react-quill-new';
import { actions, Form } from "react-redux-form";
import Alert from "react-s-alert";
import Button from 'reactstrap/lib/Button';
import useChildRouteData from '../../../hooks/useChildRouteData';
import { IRegistrationFormState } from '../../../reducers/reactReduxForms';
import * as HelpPageActions from '../../../actions/helpDrawer';
import * as QuillDocumentActions from '../../../actions/quillDocument';
import { FontAwesomeIcon, FontAwesomeIcons } from '../../../constants/fontAwesomeIcons';
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from '../../../enumerations/RequestState';
import { renderRegistrationFormSaveBlock } from '../../../helpers/alert';
import * as AuthenticationHelper from "../../../helpers/authentication";
import handleImageUpload from '../../../helpers/handleImageUpload';
import useDispatch, { useActionCreator } from '../../../hooks/useDispatch';
import useSelector from '../../../hooks/useSelector';
import NotificationAlert from '../Alerts/NotificationAlert/NotificationAlert';
import { TextFormInput } from '../../../components/form';
import { RegistrationComponent, renderFormControls } from '../../../helpers/formHelper';
import { RegistrationFormComponent } from '../../../interfaces/forms/IRegistrationComponent';

import './QuillDocument.scss';

const reduxFormName: string = "reduxForms.quillDocument";

const QuillDocument: FC = () => {
    
    const childRouteData = useChildRouteData();

    const type = Dtos.QuillDocumentType.Help;
    const quillRef = useRef<ReactQuill>(null);
    const editorContentRef = useRef<Delta | null>(null);
    const formRef = useRef<Dtos.QuillDocument & IRegistrationFormState | null>(null);

    const [initalisedQuillValue, setInitalisedQuillValue] = useState<boolean>(false);
    const [quillEditorTheme, setQuillEditorTheme] = useState<string>("bubble");
    const [editing, setEditing] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const quillDocument = useSelector(state =>
        state.quillDocument.formData instanceof Array
            ? null
            : state.quillDocument.formData
    );
    const page = useSelector(state => state.helpDrawer.page);
    const form = useSelector(state => state.reduxForms.quillDocument);
    const lookups = useSelector(state => state.quillDocument.lookups);
    const formProperties = useSelector(state => state.quillDocument.formProperties);
    const validationFailures = useSelector(state => 
        quillDocument
            ? state.quillDocument.validationFailures
            : undefined
    );
    const loadingQuillDocument = useSelector(state => state.quillDocument.loadState.status == RequestState.Pending);
    const savingQuillDocument = useSelector(state => state.quillDocument.saveState.status == RequestState.Pending);
    const saveQuillDocumentSuccess = useSelector(state => state.quillDocument.saveState.status == RequestState.Success);
    const saveQuillDocumentFailure = useSelector(state => state.quillDocument.saveState.status == RequestState.Failure);
    const validatingQuillDocument = useSelector(state => state.quillDocument.validateState.status == RequestState.Pending);
    const permissions = useSelector(state => state.user.userSummary.permissions);

    const dispatch = useDispatch();
    const getQuillDocumentByTypeAndPage = useActionCreator(QuillDocumentActions.LoadQuillDocumentByTypeAndPage, dispatch);
    const clearQuillDocument = useActionCreator(QuillDocumentActions.Clear, dispatch);
    const createQuillDocument = useActionCreator(QuillDocumentActions.CreateQuillDocument, dispatch);
    const saveQuillDocument = useActionCreator(QuillDocumentActions.SaveQuillDocument, dispatch);
    const validateQuillDocument = useActionCreator(QuillDocumentActions.ValidateQuillDocument, dispatch);
    const loadForm = useActionCreator(actions.load, dispatch);
    const resetForm = useActionCreator(actions.reset, dispatch);
    const changeForm = useActionCreator(actions.change, dispatch);
    const setPage = useActionCreator(HelpPageActions.SetPage, dispatch);

    useEffect(() => {
        if (!childRouteData?.helpPage) {
            return;
        }

        setPage(childRouteData?.helpPage);

    }, [setPage, childRouteData?.helpPage]);

    const canUpdate = useMemo(
        () => AuthenticationHelper.hasPermission(permissions, Dtos.Permission.QuillDocumentsUpdate),
        [permissions]
    );
    const canCreate = useMemo(
        () => AuthenticationHelper.hasPermission(permissions, Dtos.Permission.QuillDocumentsCreate),
        [permissions]
    );

    const editDisabled = useMemo(
        () => page != childRouteData?.helpPage,
        [page, childRouteData]
    )

    const loadReduxForm = useCallback(() => {
        if (quillDocument) {
            const extraData: Dtos.QuillDocument = {} as Dtos.QuillDocument;
            loadForm(reduxFormName, Object.assign({ ...quillDocument }, extraData));
        }
    }, [quillDocument, loadForm]);

    // Updated form ref
    useEffect(() => {
        formRef.current = form;
    }, [form]);

    // Load quill document
    useEffect(() => {
        if (page && !loadingQuillDocument) {
            // Clear editior on page change
            clearQuillDocument();
            resetForm(reduxFormName);
            setInitalisedQuillValue(false);
            setEditing(false);
            setQuillEditorTheme("bubble");

            //Load new quill doc
            getQuillDocumentByTypeAndPage(type, page);
        }
    }, [page]);

    // Initialise form
    useEffect(() => {
        if (quillDocument) {
            loadReduxForm();
            setInitalisedQuillValue(false);
        }
    }, [quillDocument]);

    // Update quill editor theme
    useEffect(() => {
        if (canUpdate && editing) {
            setQuillEditorTheme("snow");
        }
    }, [canUpdate, editing]);

    // Set initial editor value
    useEffect(() => {
        if (form?.editorState && quillRef.current?.editor && !initalisedQuillValue) {
            const parsed = JSON.parse(form.editorState) as Delta;
            quillRef.current.editor.setContents(parsed);
            editorContentRef.current = parsed;
            quillRef.current.editor.setSelection(quillRef.current.editor.getLength(), 0);

            setInitalisedQuillValue(true);
        }
    }, [form?.editorState, initalisedQuillValue]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearQuillDocument();
            resetForm(reduxFormName);
        };
    }, []);

    // Save success
    useEffect(() => {
        if (!savingQuillDocument && saveQuillDocumentSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Help document saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );
            setEditing(false);
            setQuillEditorTheme("bubble");
        }
    }, [savingQuillDocument, saveQuillDocumentSuccess]);

    // Save failure
    useEffect(() => {
        if (!savingQuillDocument && saveQuillDocumentFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingQuillDocument, saveQuillDocumentFailure]);

    // Cancel editing and reset editor state
    const onCancel = () => {
        setEditing(false);
        setQuillEditorTheme("bubble");
        loadReduxForm();
        if (quillDocument?.editorState) {
            const parsed = JSON.parse(quillDocument.editorState) as Delta;
            quillRef.current?.editor?.setContents(parsed);
        } else {
            quillRef.current?.editor?.setContents(null);
        }
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            const delta = editorContentRef.current ?? quillRef.current?.editor?.getContents();
            const parsed = JSON.stringify(delta);

            let images = formRef.current.quillDocumentImages ?? [];
            const imagesToSave = images.filter((img: any) =>
                parsed.includes(img.name)
            );
            
            const changes: Dtos.QuillDocument = {
                ...data,
                quillDocumentImages: imagesToSave,
                editorState: parsed
            };
            await saveQuillDocument(changes);
        }
    };

    const handleOnSubmitFailed = () => {
        Alert.error(
            <NotificationAlert
                minWidth={500}
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.QuillDocument = {} as Dtos.QuillDocument;

            if (form && hasBeenSubmitted && !validatingQuillDocument) {
                await validateQuillDocument(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form?.title]);

    const handleCreateClicked = () => {
        createQuillDocument(type, page);
        setEditing(true);
    };

    const onAfterImageUpload = useCallback((quillDocumentImage: Dtos.QuillDocumentImage) => {
        const currentForm = formRef.current;
        const images = [...(currentForm.quillDocumentImages ?? []), quillDocumentImage];

        changeForm(`${reduxFormName}.quillDocumentImages`, images);

        if (quillRef.current?.editor) {
            const delta = quillRef.current.editor.getContents();
            editorContentRef.current = delta;
        }
    }, [changeForm, quillDocument, reduxFormName, quillRef, editorContentRef, formRef]);

    const handleChange = useCallback((_value: string, _delta: Delta, _source: EmitterSource, editor: ReactQuill.UnprivilegedEditor) => {
            changeForm(`${reduxFormName}.editorState`, JSON.stringify(editor.getContents()));
            editorContentRef.current = editor.getContents();
    }, [changeForm]);

    const modules = useRef({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: () => handleImageUpload(quillRef, onAfterImageUpload, quillDocument?.id),
                link: (value: any) => {
                    if (value) {
                        const rawUrl = prompt('Enter the URL');
                        const href =
                            rawUrl?.startsWith("https://") || rawUrl?.startsWith("http://")
                                ? rawUrl
                                : `https://${rawUrl}`;
                        if (href) {
                            quillRef.current?.editor?.format('link', href);
                        }
                    } else {
                        quillRef.current?.editor?.format('link', false);
                    }
                }
            }
        }
    }).current;

    const renderEditor = useMemo(() => (
        <ReactQuill
            ref={quillRef}
            modules={modules}
            readOnly={!canUpdate || !editing}
            theme={quillEditorTheme}
            onChange={handleChange}
            placeholder="Start writing your content here..."
        />
    ), [canUpdate, editing, quillEditorTheme, handleChange]);

    const renderContent = useMemo(() => {
        if (!quillDocument) {
            return (
                <>
                    <div className='card card-shadow p-3'>
                        <div className="alert alert-info d-flex mb-0">
                            <div className="mr-2">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                    fixedWidth
                                />
                            </div>
                            <span className="text-wrap">No assistance has been documented for this page.</span>
                        </div>
                    </div>
                    {canCreate && (
                        <Button
                            className="btn btn-primary w-100 my-3"
                            onClick={handleCreateClicked}
                        >
                            Create
                        </Button>
                    )}
                </>
            );
        }

        const formDisabled: boolean = !canUpdate || savingQuillDocument || !editing;
        let formComponents: { [index: string]: RegistrationFormComponent } = {
            title: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className='card card-shadow p-3'>
                            <div className="form-row">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "quillDocument",
                                        {
                                            title: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        actions.change
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(_val) => handleOnSubmitFailed()}
            >
                {renderFormControls(
                    form,
                    "committee",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    actions.change
                )}

                <div className='card card-shadow p-3 mt-3'>
                    { renderEditor }
                </div>

                {canUpdate && (
                    <div className="mt-4 mb-4 text-right">
                        {editing ? (
                            <>
                                <button
                                    onClick={() => {}}
                                    type="submit"
                                    className="btn btn-primary mr-2"
                                >
                                    Save
                                </button>
                                <Button
                                    onClick={onCancel}
                                    color='primary'
                                    outline={true}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={() => setEditing(true)}
                                color='primary'
                                disabled={editDisabled}
                                style={{ cursor: editDisabled ? 'not-allowed' : 'pointer' }}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                )}
            </Form>
        );
    }, [
        canUpdate, 
        canCreate, 
        quillDocument, 
        renderEditor, 
        editing,
        editDisabled,
        reduxFormName,
        form,
        lookups,
        validationFailures,
        formProperties,
        setEditing,
        handleOnSubmit,
        handleOnSubmitFailed,
        onCancel,
        renderFormControls
    ]);

    return (
        <>
            { renderContent }
        </>
    );
};

export default QuillDocument;
